/** Patch parcial del form Home PN a partir del DTO de campaña. */
export interface HomeCuentaNaturalCampaniaPatch {
  primerNombre: string;
  segundoNombre: string;
  primerApellido: string;
  segundoApellido: string;
  celular: string;
  correo: string;
  /** Id crudo del API (número), igual que registro legacy. */
  tipoDoc: string | number;
  numDoc: string;
  confirmaDoc: string;
  /** SI / NO — radios del form Home PN. */
  tieneRUT: string;
  origen: string;
}

/** Patch parcial del form Home PJ (paso 1 + paso 2) desde DTO de campaña. */
export interface HomeCuentaJuridicaCampaniaPatch {
  step1: {
    primerNombre: string;
    segundoNombre: string;
    primerApellido: string;
    segundoApellido: string;
    celular: string;
    correo: string;
    tipoDoc: string | number;
    numDoc: string;
    confirmaDoc: string;
  };
  step2: {
    nit: string;
    origen: string;
  };
}

/**
 * Campos de campaña que no están en el form Home reducido pero sí deben
 * enviarse en el registro (misma data que guardaba el form legacy).
 */
export interface HomeCuentaCampaniaExtrasNatural {
  idGenero?: string;
  idDepartamento?: number;
  idCiudad?: number;
  codigoCiiu?: string;
  valorVentas?: number;
  anioVentas?: string;
  tieneRUT?: string;
}

export interface HomeCuentaCampaniaExtrasJuridica {
  idGenero?: string;
  idDepartamento?: number;
  idCiudad?: number;
  codigoCiiu?: string;
  valorVentas?: number;
  anioVentas?: string;
  razonSocial?: string;
  digitoVerificacion?: number;
}

function str(value: unknown): string {
  if (value == null) {
    return '';
  }
  return String(value).trim();
}

function optionalLong(value: unknown): number | undefined {
  if (value == null || value === '') {
    return undefined;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function origenFromFuente(idFuente: unknown): string {
  if (idFuente == null || idFuente === '') {
    return '';
  }
  return String(idFuente);
}

/** Campaña usa S/N; el registro PN espera SI/NO (radios legacy). */
function normalizeTieneRut(value: unknown): string {
  const v = str(value).toUpperCase();
  if (!v) {
    return '';
  }
  if (v === 'S' || v === 'SI' || v === '1' || v === 'TRUE') {
    return 'SI';
  }
  if (v === 'N' || v === 'NO' || v === '0' || v === 'FALSE') {
    return 'NO';
  }
  return v;
}

/** Id tipo doc como en el registro legacy (número si aplica). */
function tipoDocFromCampania(data: Record<string, unknown>): string | number {
  const raw = data['idTipoDocIdentidad'] ?? data['idTipoDocumento'];
  if (raw == null || raw === '') {
    return '';
  }
  const n = Number(raw);
  return Number.isFinite(n) ? n : str(raw);
}

/**
 * Extras PN desde DTO de campaña (genero→idGenero, ventasAnuales→valorVentas, etc.).
 */
export function mapCampaniaNaturalExtras(
  data: Record<string, unknown> | null | undefined
): HomeCuentaCampaniaExtrasNatural | null {
  if (!data) {
    return null;
  }
  const genero = str(data['genero'] ?? data['idGenero']);
  const tieneRut = normalizeTieneRut(data['tieneRut'] ?? data['tieneRUT']);
  const ventas = optionalLong(data['ventasAnuales'] ?? data['valorVentas']);
  const extras: HomeCuentaCampaniaExtrasNatural = {};
  if (genero) {
    // neo_genero.id_genero es string (ej. F / M), igual que en campaña.
    extras.idGenero = genero;
  }
  const depto = optionalLong(data['idDepartamento']);
  if (depto != null) {
    extras.idDepartamento = depto;
  }
  const ciudad = optionalLong(data['idCiudad']);
  if (ciudad != null) {
    extras.idCiudad = ciudad;
  }
  const ciiu = str(data['codigoCiiu']);
  if (ciiu) {
    extras.codigoCiiu = ciiu;
  }
  if (ventas != null) {
    extras.valorVentas = ventas;
  }
  const anio = str(data['anioVentas']);
  if (anio) {
    extras.anioVentas = anio;
  }
  if (tieneRut) {
    extras.tieneRUT = tieneRut;
  }
  return Object.keys(extras).length > 0 ? extras : null;
}

/**
 * Extras PJ desde DTO de campaña.
 */
export function mapCampaniaJuridicaExtras(
  data: Record<string, unknown> | null | undefined
): HomeCuentaCampaniaExtrasJuridica | null {
  if (!data) {
    return null;
  }
  const genero = str(data['genero'] ?? data['idGenero']);
  const ventas = optionalLong(data['ventasAnuales'] ?? data['valorVentas']);
  const extras: HomeCuentaCampaniaExtrasJuridica = {};
  if (genero) {
    extras.idGenero = genero;
  }
  const depto = optionalLong(data['idDepartamento']);
  if (depto != null) {
    extras.idDepartamento = depto;
  }
  const ciudad = optionalLong(data['idCiudad']);
  if (ciudad != null) {
    extras.idCiudad = ciudad;
  }
  const ciiu = str(data['codigoCiiu']);
  if (ciiu) {
    extras.codigoCiiu = ciiu;
  }
  if (ventas != null) {
    extras.valorVentas = ventas;
  }
  const anio = str(data['anioVentas']);
  if (anio) {
    extras.anioVentas = anio;
  }
  const razon = str(data['razonSocial']);
  if (razon) {
    extras.razonSocial = razon.toLocaleUpperCase();
  }
  const dv = optionalLong(data['digitoVerificacion']);
  if (dv != null) {
    extras.digitoVerificacion = dv;
  }
  return Object.keys(extras).length > 0 ? extras : null;
}

/**
 * Mapea respuesta `consultarInformacionCampania/personaNatural` → campos del form Home.
 */
export function mapCampaniaNaturalToHomeForm(
  data: Record<string, unknown> | null | undefined
): HomeCuentaNaturalCampaniaPatch | null {
  if (!data) {
    return null;
  }
  const numDoc = str(data['numeroDocumento']);
  return {
    primerNombre: str(data['primerNombre']),
    segundoNombre: str(data['segundoNombre']),
    primerApellido: str(data['primerApellido']),
    segundoApellido: str(data['segundoApellido']),
    celular: str(data['numeroCelular']).replace(/\D/g, '').slice(0, 10),
    correo: str(data['correoElectronico']).toLowerCase(),
    tipoDoc: tipoDocFromCampania(data),
    numDoc,
    confirmaDoc: numDoc,
    tieneRUT: normalizeTieneRut(data['tieneRut'] ?? data['tieneRUT']),
    origen: origenFromFuente(data['idFuenteNeocredito']),
  };
}

/**
 * Mapea respuesta `consultarInformacionCampania/personaJuridica` → form Home PJ.
 * personaDeContacto / contacto no vienen en el DTO de campaña → quedan vacíos.
 */
export function mapCampaniaJuridicaToHomeForm(
  data: Record<string, unknown> | null | undefined
): HomeCuentaJuridicaCampaniaPatch | null {
  if (!data) {
    return null;
  }
  const numDoc = str(data['numeroDocumento']);
  const nit = str(data['nit']).replace(/\D/g, '').slice(0, 9);
  return {
    step1: {
      primerNombre: str(data['primerNombre']),
      segundoNombre: str(data['segundoNombre']),
      primerApellido: str(data['primerApellido']),
      segundoApellido: str(data['segundoApellido']),
      celular: str(data['numeroCelular']).replace(/\D/g, '').slice(0, 10),
      correo: str(data['correoElectronico']).toLowerCase(),
      tipoDoc: tipoDocFromCampania(data),
      numDoc,
      confirmaDoc: numDoc,
    },
    step2: {
      nit,
      origen: origenFromFuente(data['idFuenteNeocredito']),
    },
  };
}

/** Interpreta `exitoso` del API (boolean o string "false"). */
export function isCampaniaResponseOk(response: {
  exitoso?: unknown;
}): boolean {
  const v = response?.exitoso;
  return v === true || v === 'true';
}

/**
 * Igual que el registro legacy: usa el id del catálogo con igualdad laxa (==).
 * Importante NeoCrédito: 1=NIT, 2=Cédula, 5=Pasaporte. En PN el NIT no está en el select.
 */
export function resolveTipoDocOptionValue(
  documentos: Array<{ idTipoDocIdentidad?: string | number | null }>,
  raw: unknown
): string | number {
  if (raw == null || raw === '') {
    return '';
  }
  const match = documentos.find(
    (d) =>
      d.idTipoDocIdentidad != null &&
      // igualdad laxa: API number vs option value string
      d.idTipoDocIdentidad == raw
  );
  if (match?.idTipoDocIdentidad != null) {
    // String para alinear con <option [value]> (DOM siempre string).
    return String(match.idTipoDocIdentidad);
  }
  // Id de campaña no existe en el select (ej. NIT=1 en formulario PN) → no forzar valor huérfano.
  return '';
}
